import { useHistory, useLocation, useParams } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import type {
  MarketingEmailTemplate,
  MarketingEmailTemplateContentType,
} from "@/react/types/MarketingEmailTemplates";
import { Button } from "@circle-react-uikit/Button";
import { Loader } from "@circle-react-uikit/Loader";
import { Modal } from "@circle-react-uikit/ModalV2";
import { CardPreview } from "./TemplateContentElements/CardPreview";
import { useTemplateDetail } from "./hooks/useTemplateDetail";

const localeNamespace = "settings.emails.templates";

export function PreviewTemplate({
  onApply,
}: {
  onApply: (
    templateId: number,
    contentType: MarketingEmailTemplateContentType,
  ) => void;
}) {
  const history = useHistory();
  const location = useLocation();
  const { templateId } = useParams<{
    templateId: string;
    templateType: string;
  }>();

  const { template } = location.state
    ? (location.state as { template: MarketingEmailTemplate })
    : { template: undefined };

  const { data, isLoading, isError } = useTemplateDetail(Number(templateId), {
    placeholderData: template,
  });

  // TODO: Review default value when working on the visual email builder preview: https://www.notion.so/circleco/Preview-visual-editor-template-261b655313b780ed88a0f6f47c8c54e2
  const templateContentType = template?.content_type ?? "tip_tap_email";

  const handleReturnToLibrary = () => {
    history.push(`/templates`);
  };

  if (isLoading) {
    return <Loader />;
  }

  if (isError) {
    return null;
  }

  const isVisualEmailBuilder = data?.content_type === "visual_email_builder";

  return (
    <>
      <div className="border-b-primary text-dark flex items-center justify-between border-b px-8 pb-5 pt-6">
        <div className="flex items-center gap-2">
          <Modal.BackButton onClick={handleReturnToLibrary} />
          <Modal.Title size="sm">
            {t([localeNamespace, "preview_template"])}
          </Modal.Title>
        </div>
        <Modal.CloseButton onClick={handleReturnToLibrary} />
      </div>
      <Modal.Body className="px-6 py-4">
        <div className="bg-secondary h-full overflow-auto rounded-xl py-8">
          {data && (
            <div
              className={`bg-primary mx-auto min-h-full overflow-hidden rounded-xl ${
                isVisualEmailBuilder ? "max-w-[640px]" : "max-w-2xl p-6"
              }`}
            >
              <CardPreview template={data} />
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer className="border-t-primary flex justify-end gap-2 border-t pb-6 pt-4">
        <Button variant="secondary" large onClick={handleReturnToLibrary}>
          {t("cancel")}
        </Button>
        <Button
          variant="circle"
          large
          onClick={() => onApply(Number(templateId), templateContentType)}
        >
          {t([localeNamespace, "use_this_template"])}
        </Button>
      </Modal.Footer>
    </>
  );
}
